/**
 * What: Derives a serializable resource-index definition and its renderer
 * registry from a single column array, so definition ids and renderer keys
 * cannot drift. Behaviour (row data, cell content) stays in the supplied
 * render functions — the factory only splits shape from behaviour.
 * Transport / data-flow: Pure; validated once via parseResourceIndexSchema.
 */
import type { ReactNode } from 'react'
import { parseResourceIndexSchema, type IndexColumn, type ResourceIndexSchema } from './indexSchema'

export type ResourceIndexColumn<R> = IndexColumn & {
  render: (model: R) => ReactNode
}

type DefineResourceIndexInput<R> = {
  id: string
  resource?: { singular: string; plural: string }
  views?: Array<{ id: string; label: string }>
  filters?: Array<{
    key: string
    label: string
    options?: Array<{ value: string; label: string }>
    multiple?: boolean
  }>
  sortOptions?: Array<{ label: string; value: string }>
  searchLabel?: string
  metadata?: { selection?: 'none'; pagination?: 'client' }
  columns: Array<ResourceIndexColumn<R>>
}

/** Splits one column array into a parsed JSON definition plus an exhaustive renderer map. Throwing. */
export function defineResourceIndex<R>(input: DefineResourceIndexInput<R>): {
  definition: ResourceIndexSchema
  renderers: Record<string, (model: R) => ReactNode>
} {
  const seen = new Set<string>()
  for (const column of input.columns) {
    if (seen.has(column.id)) throw new Error(`Duplicate resource-index column "${column.id}"`)
    seen.add(column.id)
  }
  const { columns, ...rest } = input
  const definition = parseResourceIndexSchema({
    ...rest,
    columns: columns.map(({ id, heading, kind, listSlot, showInList, toneMap }) => ({
      id,
      heading,
      kind,
      ...(listSlot === undefined ? {} : { listSlot }),
      ...(showInList === undefined ? {} : { showInList }),
      ...(toneMap === undefined ? {} : { toneMap }),
    })),
  })
  const renderers = Object.fromEntries(columns.map((column) => [column.id, column.render])) as Record<
    string,
    (model: R) => ReactNode
  >
  return { definition, renderers }
}
